import React, { ReactElement, useMemo } from 'react';
import { EventTask, TeamPermission, useProjectTasksService, useTimezoneService } from 'nekst-api';
import {
  FormRows,
  TextInput,
  TimeInput,
  RelativeRow,
  DateInput,
  FormTemplate,
  AfterSubmitFunc, useFormContextRequired,
} from 'ui-builder';
import { InformationMessage, ProjectIdContext, Separator, TimezoneDropdown } from 'features/nekst-widgets';
import ProjectDropdown from '../ProjectDropdown';
import {
  EventProjectTaskSmartTagsWysiwyg,
  EventTaskConfigurationInfoMessage, PortalRoadmapCheckbox,
  ProjectEventTransactionPartiesInput,
  RoadmapDescription
} from 'features/tasks-forms';
import OrganizerInput from './OrganizerInput';
import TaskCommentsListInput from '../comments/TaskCommentsListInput';
import useSettingsNavigator from "../../../../../settings/settingsNavigator";
import { useAuthorizationChecker } from "authorization-scope";
import Checkbox from "../../../../../shared/uibuilder/form/input/Checkbox";
import { EmptyCell } from "../../../../../shared/web/uitheme/form/RelativeRow";

export interface BaseEventTaskFormProps {
  submitFormFunc: (data: EventTask) => Promise<EventTask>;
  afterSubmitFunc?: AfterSubmitFunc;
  data?: EventTask;
  title: string | ReactElement;
  buttons: ReactElement;
  initialData?: EventTask;
  isDisabled?: boolean | ((data: EventTask) => boolean);
  showProjectDropdown?: boolean;
}

function DueDateRow() {
  const formContext = useFormContextRequired<EventTask>();

  const timezoneService = useTimezoneService();

  const shouldDisplayTimezoneDropdown = useMemo(() =>
      formContext.data?.dueDate?.timezone
      && formContext.data?.dueDate?.time
      && formContext.data?.dueDate?.timezone !== timezoneService.getTimezone(),
    []);

  return (
    <>
      <RelativeRow weights={[4, 3, 3, 2]}>
        <DateInput source="dueDate.date" label="Date" />
        <TimeInput
          source="dueDate.time"
          label="Start Time"
        />
        <TimeInput
          source="dueDate.endTime"
          label="End Time"
        />
      </RelativeRow>
      {shouldDisplayTimezoneDropdown && (
        <RelativeRow weights={[6, 6]}>
          <TimezoneDropdown source="dueDate.timezone" />
        </RelativeRow>
      )}
    </>

  );
}

export default function BaseEventTaskForm(props: BaseEventTaskFormProps) {
  const projectTasksService = useProjectTasksService();

  const isDisabled = props.isDisabled ?? false;

  const showProjectDropdown = props.showProjectDropdown || false;

  const settingsNavigator = useSettingsNavigator();

  const { isGranted } = useAuthorizationChecker();

  return (
    <ProjectIdContext projectId={props.initialData?.projectId || 0}>
      <FormTemplate<EventTask & { copyRoadmapDescription?: boolean }>
        title={props.title}
        submitFormFunc={props.submitFormFunc}
        afterSubmitFunc={props.afterSubmitFunc}
        buttons={isGranted(TeamPermission.MANAGE_EVENT_TASKS) ? props.buttons : null}
        isDisabled={isDisabled}
        initialData={{
          copyRoadmapDescription: !props.initialData?.roadmapDescription,
          ...(props.initialData || props.data)
        }}
        validationSchema={projectTasksService.getEventTaskValidationSchema()}
        dependencies={[props.initialData?.projectId]}
      >

        {!isGranted(TeamPermission.MANAGE_EVENT_TASKS) && (
          <InformationMessage>
            <EventTaskConfigurationInfoMessage
              openSettingsFunc={() => {
                settingsNavigator.openCalendarIntegrationPage();
              }}
            />
          </InformationMessage>
        )}
        <FormRows>
          {showProjectDropdown && (<ProjectDropdown source="projectId" />)}
          <TextInput source="name" label="Appointment Name" />
          <DueDateRow />
          <FormRows>
            <RelativeRow weights={[2.5, 2, 3, 4.5]}>
              <Checkbox source="isKeyDate" label="Key Date" />
              <Checkbox source="isPrivate" label="Private" />
              <EmptyCell />
              <PortalRoadmapCheckbox />
            </RelativeRow>
          </FormRows>
          <OrganizerInput />
          <TextInput source="location" label="Location" />
          <Separator />
          <EventProjectTaskSmartTagsWysiwyg
            source="description"
            label="Appointment Description"
            projectId={(data) => data.projectId}
          />
          <RoadmapDescription />
          <Separator />
          <ProjectEventTransactionPartiesInput />
          <Separator isVisible={(data) => data.id} />
          <TaskCommentsListInput isVisible={(data) => data.id} />
        </FormRows>
      </FormTemplate>
    </ProjectIdContext>
  );
}
